/**
 * AnnouncementsBell — AppBar icon that opens the announcements modal.
 *
 * Shows a badge with the count of unseen announcements. Hidden entirely when
 * the user has no eligible announcements. Part of PanLuma's delivery surface.
 *
 * Two variants (same fold pattern as AppBarMailFilesShortcuts):
 * - `inline` (default): icon button for the desktop header cluster.
 * - `menu`: flat list row for the mobile header overflow menu, so the bell
 *   never competes for mobile app-bar width.
 */
import { useTranslation } from 'react-i18next';
import { Megaphone } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import { CountBadge } from '../../../../shared/components/common/CountBadge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../components/ui/tooltip';
import { useUserAnnouncements } from './AnnouncementsProvider';

interface AnnouncementsBellProps {
  moduleColor: string;
  variant?: 'inline' | 'menu';
}

export function AnnouncementsBell({ moduleColor, variant = 'inline' }: AnnouncementsBellProps) {
  const { t } = useTranslation('common');
  const { announcements, unseenCount, open } = useUserAnnouncements();

  if (announcements.length === 0) {
    return null;
  }

  if (variant === 'menu') {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start gap-2 font-normal text-foreground"
        onClick={open}
      >
        <Megaphone className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-start">{t('announcementsModal.bellTooltip')}</span>
        <CountBadge count={unseenCount} ariaLabel={t('notifications.unreadCount', { count: unseenCount })} />
      </Button>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative hover:bg-accent"
          style={{ color: moduleColor }}
          aria-label={t('announcementsModal.bellAriaLabel')}
          onClick={open}
        >
          <Megaphone className="h-4 w-4" />
          <CountBadge count={unseenCount} className="absolute -top-0.5 -end-0.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{t('announcementsModal.bellTooltip')}</TooltipContent>
    </Tooltip>
  );
}
